import { AxiosResponse, CancelTokenSource } from 'axios';
import API from './index';
import { useQuery } from '@tanstack/react-query';

const api = new API();

export namespace EventAPINS {
    export enum StatusLabel {
        FewTicketsLeft = 1,
        SoldOut = 2,
    }

    export enum PrimaryColorContrast {
        Light = 1,
        Dark = 2,
    }

    export enum Placement {
        Hero = 1,
        Secondary = 2,
    }

    type stringOrNull = string | null;

    export interface EventUISettingsType {
        primaryColor: stringOrNull;
        primaryColorContrast: PrimaryColorContrast | null;
        secondaryColor: stringOrNull;

        statusLabel: EventAPINS.StatusLabel | null;
        category: string | null;
        placement: Placement | null; // Lower number represents higher real estate value
    }

    export interface EventAvailabilityModel {
        id: number;
        eventId: number;
        availabilityId: number;
    }

    export interface EventRaw {
        id: number;
        name: string;
        shortDescription: string | null;
        description: string | null;
        paymentType: number;
        paymentAmount: number;
        imagePublicId: string | null;
        uiSettings: EventUISettingsType | null;
        availabilities: EventAvailabilityModel;
        restaurantId: number;
        slug: string;
    }

    export interface EventUpcomingType {
        event: EventAPINS.EventRaw & {
            from: string | null;
            to: string | null;
            lunchOrDinner: 'lunch' | 'dinner';
        };
        restaurantId: number;
        restaurantName: string;
        restaurantCoverImgUrl: string;
        restaurantLogoImgUrl: string;
        restaurantSlug: string;
    }
}

export const getEvent: (
    eventSlug: string,
    restaurantSlug: string,
    cancelToken: CancelTokenSource,
) => Promise<
    AxiosResponse<{
        event: EventAPINS.EventRaw;
        onlineMaxGuests: number;
        restaurantLogoUrl: string | null;
        restaurantCoverUrl: string | null;
        restaurantName: string;
        availableDates: string[];
    }>
> = (eventSlug, restaurantSlug, cancelToken) => {
    return api._get(`/web/event/details/${restaurantSlug}/${eventSlug}`, {
        cancelToken: cancelToken.token,
    });
};

export const getAllUpcomingEvents: (
    cancelToken: CancelTokenSource,
) => Promise<AxiosResponse<EventAPINS.EventUpcomingType[]>> = (cancelToken) => {
    return api._get(`/web/event`, {
        cancelToken: cancelToken.token,
    });
};

export const getAllUpcomingXMas: (
    cancelToken: CancelTokenSource,
) => Promise<AxiosResponse<EventAPINS.EventUpcomingType[]>> = (cancelToken) => {
    return api._get(`/web/event/upcomingxmas`, {
        cancelToken: cancelToken.token,
    });
};

const getEventsHappeningAt: (
    restaurantId: number,
    date: string,
    cancelToken: CancelTokenSource,
) => Promise<AxiosResponse<EventAPINS.EventRaw[]>> = (
    restaurantId,
    date,
    cancelToken,
) => {
    return api._get(`/web/event/${restaurantId}/${date}`, {
        cancelToken: cancelToken.token,
    });
};

export const useGetEventsHappeningAtQuery = (
    restaurantId: number,
    date: string,
    cancelToken: CancelTokenSource,
) => {
    return useQuery({
        queryKey: ['eventAtDate', restaurantId, date],
        queryFn: async () => {
            try {
                const res = await getEventsHappeningAt(
                    restaurantId,
                    date,
                    cancelToken,
                );

                if (res.status >= 200 && res.status <= 299) {
                    return res.data ?? [];
                }

                throw new Error(res.statusText);
            } catch (e) {
                console.error('Could not load events at given date', e);
                throw e;
            }
        },
    });
};
